import { Button } from '@components/ui/button';
import { cn } from '@utils/cn';
import { getHref, ROUTE } from '@utils/route-utils';
import { ChevronRight, LockKeyhole, LucideIcon, Sparkle } from 'lucide-react';
import Link from 'next/link';
import { ReactNode } from 'react';

export type UpgradeHighlight = {
  icon: LucideIcon;
  label: string;
};

type UpgradeStateProps = {
  organizationId: string;
  /** May carry a `<span className="text-brand-text">` emphasis. */
  title: ReactNode;
  description?: string;
  highlights?: readonly UpgradeHighlight[];
  cta: string;
  /** Small print under the button, e.g. which plan includes the feature. */
  footnote?: string;
  className?: string;
};

/**
 * Promotional paywall card shown in place of a gated page body, and on every
 * page of a Free organization. The yellow rim and glow are a deliberate
 * exception to the no-outline card rule: this is the one upsell surface.
 */
export default function UpgradeState({
  organizationId,
  title,
  description,
  highlights = [],
  cta,
  footnote,
  className
}: UpgradeStateProps) {
  return (
    <section
      className={cn(
        'relative isolate overflow-hidden rounded-xl bg-card px-6 py-10 text-center shadow-[0_0_48px_-16px] shadow-primary/50 ring-1 ring-primary/40',
        className
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-28 -z-10 mx-auto size-72 rounded-full bg-primary/10 blur-3xl"
      />
      <div className="mx-auto flex max-w-lg flex-col items-center gap-6">
        <div className="relative" aria-hidden>
          <Sparkle className="absolute -left-9 top-4 size-4 fill-current text-brand-text" />
          <span className="flex size-20 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/30">
            <LockKeyhole className="size-9 text-brand-text" strokeWidth={1.5} />
          </span>
          <Sparkle className="absolute -right-9 top-4 size-4 fill-current text-brand-text" />
        </div>

        <div className="space-y-2">
          <h2 className="text-display-sm text-foreground">{title}</h2>
          {description && (
            <p className="text-body text-muted-foreground">{description}</p>
          )}
        </div>

        {highlights.length > 0 && (
          <ul className="grid w-full grid-cols-2 gap-3 text-left">
            {highlights.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex min-h-16 items-center gap-3 rounded-lg bg-muted px-3 py-3"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Icon className="size-5 text-brand-text" aria-hidden />
                </span>
                <span className="text-body-dense text-foreground">{label}</span>
              </li>
            ))}
          </ul>
        )}

        <Button asChild variant="primary" size="lg" className="w-full">
          <Link href={getHref(organizationId, [ROUTE.UPGRADE])}>
            {cta}
            <ChevronRight className="size-5" aria-hidden />
          </Link>
        </Button>

        {footnote && (
          <p className="text-caption text-muted-foreground">{footnote}</p>
        )}
      </div>
    </section>
  );
}
